import React, { useRef, useEffect, useState } from "react";
import "./Product.css";
import { gsap } from "gsap";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const Product = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const ProductRef = useRef(null);
  const imageRef = useRef(null);
  const specsContainerRef = useRef(null);
  const batteryRef = useRef(null);
  const specRefs = useRef([]);
  const arrowRefs = useRef([]);
  const modelNameRef = useRef(null);
  const featureRefs = useRef([]);

  // Reset refs each render to avoid stale refs
  specRefs.current = [];
  arrowRefs.current = [];
  featureRefs.current = [];

  const products = [
    {
      id: 1,
      name: "EZ RAIDER LW",
      image: "./bike1.png",
      description: "A lightweight electric mobility vehicle built for versatility and ease of use. The EZ Raider LW is designed for trail riding, property access, recreation, and mixed terrain environments where stability and maneuverability matter.",
      specs: [
        { label: "DRIVE SYSTEM", value: "Rear wheel drive with dual 1200 W hub motors" },
        { label: "BATTERY", value: "60 V lithium ion battery approximately 1740 Wh" },
        { label: "MAXIMUM SPEED", value: "Up to 38 kmh / 24 mph depending on conditions" },
        { label: "MAXIMUM RANGE", value: "Up to 50 km / 30 miles per charge depending on terrain and load" },
        { label: "PAYLOAD CAPACITY", value: "Approximately 170 kg / 375 lb" },
        { label: "BRAKES", value: "Front and rear hydraulic disc brakes" },
        { label: "WEIGHT", value: "Approximately 93 kg / 205 lb" },
      ],
      features: [
        "Foldable steering column",
        "Patented suspension system",
        "Low center of gravity for stability",
        "Regenerative braking"
      ],
      battery: "60V 1740Wh"
    },
    {
      id: 2,
      name: "EZ RAIDER HD2",
      image: "./bike2.png",
      description: "A heavy duty electric mobility vehicle designed for longer range and more demanding terrain. The HD2 delivers increased capability for sand, mud, rocks, and uneven surfaces while maintaining predictable handling.",
      specs: [
        { label: "DRIVE SYSTEM", value: "All wheel drive with electric hub motors" },
        { label: "BATTERY", value: "60 V lithium ion battery approximately 3000 Wh" },
        { label: "MAXIMUM SPEED", value: "Up to 40 kmh / 25 mph depending on conditions" },
        { label: "MAXIMUM RANGE", value: "Up to 75 km / 47 miles per charge depending on terrain and load" },
        { label: "PAYLOAD CAPACITY", value: "Approximately 170 kg / 375 lb" },
        { label: "BRAKES", value: "Front and rear hydraulic disc brakes" },
        { label: "SUSPENSION", value: "Long travel adjustable suspension" },
      ],
      features: [
        "Regenerative braking",
        "Enhanced traction across loose terrain",
        "Low maintenance electric drivetrain"
      ],
      battery: "60V 3000Wh"
    },
    {
      id: 3,
      name: "EZ RAIDER HD4",
      image: "./bike3.png",
      description: "The most capable model in the EZ Raider US lineup. The HD4 is engineered for maximum traction and payload capacity with true four wheel drive performance for challenging terrain conditions.",
      specs: [
        { label: "DRIVE SYSTEM", value: "True four wheel drive with four 1200 W hub motors" },
        { label: "BATTERY", value: "60 V lithium ion battery approximately 2700 to 3000 Wh depending on configuration" },
        { label: "MAXIMUM SPEED", value: "Up to 40 kmh / 25 mph depending on conditions" },
        { label: "MAXIMUM RANGE", value: "Up to 80 km / 50 miles per charge depending on terrain and load" },
        { label: "PAYLOAD CAPACITY", value: "Approximately 200 kg / 440 lb" },
        { label: "BRAKES", value: "Front and rear hydraulic disc brakes" },
        { label: "SUSPENSION", value: "Advanced long travel suspension system" },
      ],
      features: [
        "Maximum traction and torque distribution",
        "Designed for heavier loads",
        "Stable low center of gravity"
      ],
      battery: "60V 2700-3000Wh"
    },
  ];

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initial animations
  useEffect(() => {
    gsap.fromTo(
      ProductRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 1, ease: "power2.out" }
    );

    gsap.fromTo(
      ".Product-background-future",
      { opacity: 0 },
      { opacity: 1, duration: 1, ease: "power2.out", delay: 0.3 }
    );

    gsap.fromTo(
      ".Product-header",
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.4 }
    );

    gsap.fromTo(
      ".Product-content",
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.6 }
    );

    gsap.fromTo(
      specRefs.current.filter(Boolean),
      { x: isMobile ? 0 : 20, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        stagger: isMobile ? 0.05 : 0.1,
        ease: "power2.out",
        delay: 0.8,
      }
    );

    gsap.fromTo(
      featureRefs.current.filter(Boolean),
      { x: isMobile ? 0 : 20, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        stagger: isMobile ? 0.05 : 0.1,
        ease: "power2.out",
        delay: 1.0,
      }
    );

    gsap.fromTo(
      modelNameRef.current,
      { opacity: 0, y: 20 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        delay: 0.9,
      }
    );
  }, []);

  // Slide change animation
  useEffect(() => {
    if (isAnimating) return;

    const animateSlideChange = () => {
      setIsAnimating(true);

      const tl = gsap.timeline({
        onComplete: () => {
          setTimeout(() => {
            setIsAnimating(false);
          }, 50);
        },
      });

      // Fade out current content
      tl.to([imageRef.current, specsContainerRef.current, document.querySelector('.Product-features-section')], {
        opacity: 0,
        y: isMobile ? 10 : 15,
        duration: 0.3,
        ease: "power2.in",
      });

      // Update content
      tl.add(() => {
        if (imageRef.current) imageRef.current.style.opacity = "0";
        if (specsContainerRef.current) specsContainerRef.current.style.opacity = "0";
        const featuresSection = document.querySelector('.Product-features-section');
        if (featuresSection) featuresSection.style.opacity = "0";
      });

      // Fade in new content
      tl.to([imageRef.current, specsContainerRef.current, document.querySelector('.Product-features-section')], {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power3.out",
      });

      // Model name animation
      tl.fromTo(
        modelNameRef.current,
        { opacity: 0, y: isMobile ? 15 : 20 },
        { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" },
        0.1
      );

      // Image scale animation
      tl.fromTo(
        imageRef.current,
        { scale: 0.95 },
        { scale: 1, duration: 0.4, ease: "back.out(1.2)" },
        0.1
      );

      // Spec items animation
      tl.fromTo(
        specRefs.current.filter(Boolean),
        { x: isMobile ? -10 : -15, opacity: 0, skewX: isMobile ? 0 : -3 },
        {
          x: 0,
          opacity: 1,
          skewX: 0,
          duration: 0.4,
          stagger: isMobile ? 0.03 : 0.05,
          ease: "power2.out",
        },
        0.2
      );

      // Feature items animation
      tl.fromTo(
        featureRefs.current.filter(Boolean),
        { x: isMobile ? -10 : -15, opacity: 0, skewX: isMobile ? 0 : -3 },
        {
          x: 0,
          opacity: 1,
          skewX: 0,
          duration: 0.4,
          stagger: isMobile ? 0.03 : 0.05,
          ease: "power2.out",
        },
        0.3
      );

      // Battery animation
      tl.fromTo(
        batteryRef.current,
        { scale: 0.9, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.4, ease: "power2.out" },
        0.35
      );

      // Arrow buttons animation
      tl.fromTo(
        arrowRefs.current.filter(Boolean),
        { scale: 0.9 },
        { scale: 1, duration: 0.3, ease: "back.out(1.4)" },
        0.4
      );

      // Set total animation duration
      tl.totalDuration(0.8);
    };

    animateSlideChange();
  }, [currentSlide, isMobile]);

  const nextSlide = () => {
    if (!isAnimating) {
      setCurrentSlide((prev) => (prev + 1) % products.length);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
    }
  };

  const addArrowRef = (el) => {
    if (el) arrowRefs.current.push(el);
  };

  const currentProduct = products[currentSlide];

  return (
    <section className="Product-section" ref={ProductRef}>
      <div className="Product-container">
        <div className="Product-background-future">THE FUTURE</div>

        <div className="Product-header">
          <div className="Product-products-label">
            <span className="Product-label-text">OUR PRODUCTS</span>
            <div className="Product-underline"></div>
          </div>
          <h1 className="Product-main-heading">YOUR JOURNEY. YOUR CHOICE.</h1>
        </div>

        <div className="Product-content">
          <div className="Product-image-section">
            <div className="Product-image-container">
              <div className="Product-image-wrapper">
                <img
                  ref={imageRef}
                  src={currentProduct.image}
                  alt={currentProduct.name}
                  className="Product-image"
                />
              </div>
            </div>

            <div className="Product-image-navigation-arrows">
              <button
                ref={addArrowRef}
                className="Product-image-arrow-btn Product-prev-btn"
                onClick={prevSlide}
                disabled={isAnimating}
                aria-label="Previous product"
              >
                <ArrowBackIcon />
              </button>

              <div className="Product-slide-indicator">
                {products.map((_, index) => (
                  <button
                    key={index}
                    className={`Product-indicator-dot ${
                      index === currentSlide ? "Product-active" : ""
                    }`}
                    onClick={() => !isAnimating && setCurrentSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    disabled={isAnimating}
                  />
                ))}
              </div>

              <button
                ref={addArrowRef}
                className="Product-image-arrow-btn Product-next-btn"
                onClick={nextSlide}
                disabled={isAnimating}
                aria-label="Next product"
              >
                <ArrowForwardIcon />
              </button>
            </div>

            {/* Features Section Below Image */}
            <div className="Product-features-section">
              <div className="Product-features-label">
                <span className="Product-green-slash">//</span> FEATURES
              </div>
              
              <div className="Product-features-grid">
                {currentProduct.features.map((feature, index) => (
                  <div
                    key={index}
                    ref={(el) => {
                      if (el) featureRefs.current.push(el);
                    }}
                    className="Product-feature-item"
                  >
                    <div className="Product-feature-item-inner">
                      <div className="Product-feature-label">
                        <span className="Product-feature-check">✓</span> {feature}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="Product-specs-container" ref={specsContainerRef}>
            <div className="Product-model-name" ref={modelNameRef}>
              <span className="Product-model-slash">//</span>
              {currentProduct.name}
            </div>

            <div className="Product-description">
              {currentProduct.description}
            </div>

            <div className="Product-key-specs-label">
              <span className="Product-green-slash">//</span> KEY SPECIFICATIONS
            </div>

            <div className="Product-specs-grid">
              {currentProduct.specs.map((spec, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    if (el) specRefs.current.push(el);
                  }}
                  className="Product-spec-item"
                >
                  <div className="Product-spec-item-inner">
                    <div className="Product-spec-label">
                      <span className="Product-green-slash">//</span> {spec.label}
                    </div>
                    <div className="Product-spec-value">{spec.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div ref={batteryRef} className="Product-battery-capacity">
              <div className="Product-battery-label">
                <span className="Product-green-slash">//</span> BATTERY CAPACITY
              </div>
              <div className="Product-battery-value">{currentProduct.battery}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Product;